import React, { useRef, useEffect, useState, useCallback } from "react";
import { Table, TableBody } from "@/components/ui/table";
import NotificationListHeader from "./NotificationListHeader";
import NotificationListItem from "./NotificationListItem";
import EmptyStateMessage from "./list/EmptyStateMessage";
import LoadingState from "./list/LoadingState";
import { SupabaseNotification } from "@/types/notifications";

interface NotificationTableProps {
  isLoading: boolean;
  notifications: SupabaseNotification[];
  displayedNotifications: SupabaseNotification[];
  viewMode: "active" | "archived";
  formatDate: (date: string) => string;
  sortConfig?: { key: string; direction: 'asc' | 'desc' };
  requestSort: (key: string) => void;
  handleNotificationClick: (id: string) => void;
  handleArchive: (e: React.MouseEvent, id: string) => void;
  handleRestore: (e: React.MouseEvent, id: string) => void;
  handleDelete: (e: React.MouseEvent, id: string) => void;
  handleSendWebhook?: (e: React.MouseEvent, id: string) => void;
  canUseBulkActions: boolean;
  isAllSelected: boolean;
  isPartiallySelected: boolean;
  toggleSelectAll: () => void;
  isSelected: (id: string) => boolean;
  toggleSelectItem: (id: string) => void;
  getUnreadChatCount?: (notificationId: string) => number;
}

const NotificationTable = ({
  isLoading,
  notifications,
  displayedNotifications,
  viewMode,
  formatDate,
  sortConfig,
  requestSort,
  handleNotificationClick,
  handleArchive,
  handleRestore,
  handleDelete,
  handleSendWebhook,
  canUseBulkActions,
  isAllSelected,
  isPartiallySelected,
  toggleSelectAll,
  isSelected,
  toggleSelectItem,
  getUnreadChatCount
}: NotificationTableProps) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const stickyScrollRef = useRef<HTMLDivElement>(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [isTableVisible, setIsTableVisible] = useState(false);
  const [containerRect, setContainerRect] = useState<{ left: number; width: number }>({ left: 0, width: 0 });
  const syncing = useRef(false);

  const getScrollableContainer = useCallback(() => {
    return tableContainerRef.current?.querySelector('.relative.w-full.overflow-auto') as HTMLElement | null;
  }, []);

  const updateDimensions = useCallback(() => {
    if (tableContainerRef.current) {
      const table = tableContainerRef.current.querySelector('table');
      const container = getScrollableContainer();
      if (table) setScrollWidth(table.scrollWidth);
      if (container) {
        const rect = container.getBoundingClientRect();
        setContainerRect({ left: rect.left, width: rect.width });
      }
    }
  }, [getScrollableContainer]);

  // IntersectionObserver to detect table visibility
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsTableVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ResizeObserver + scroll/resize listener for dimensions
  useEffect(() => {
    updateDimensions();
    const ro = new ResizeObserver(updateDimensions);
    const table = tableContainerRef.current?.querySelector('table');
    if (table) ro.observe(table);
    window.addEventListener('resize', updateDimensions);
    window.addEventListener('scroll', updateDimensions, true);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateDimensions);
      window.removeEventListener('scroll', updateDimensions, true);
    };
  }, [updateDimensions, displayedNotifications.length]);

  const needsScroll = scrollWidth > containerRect.width;
  const showFixedScrollbar = isTableVisible && needsScroll;

  const handleTableScroll = useCallback(() => {
    if (syncing.current) return;
    syncing.current = true;
    const container = getScrollableContainer();
    if (container && stickyScrollRef.current) {
      stickyScrollRef.current.scrollLeft = container.scrollLeft;
    }
    syncing.current = false;
  }, [getScrollableContainer]);

  const handleStickyScroll = useCallback(() => {
    if (syncing.current) return;
    syncing.current = true;
    const container = getScrollableContainer();
    if (container && stickyScrollRef.current) {
      container.scrollLeft = stickyScrollRef.current.scrollLeft;
    }
    syncing.current = false;
  }, [getScrollableContainer]);

  useEffect(() => {
    const container = getScrollableContainer();
    if (container) {
      container.addEventListener('scroll', handleTableScroll);
      return () => container.removeEventListener('scroll', handleTableScroll);
    }
  }, [handleTableScroll, getScrollableContainer, displayedNotifications.length]);

  return (
    <div className="rounded-md border" ref={tableContainerRef}>
      <Table>
        <NotificationListHeader 
          onSort={requestSort}
          sortConfig={sortConfig}
          showBulkSelect={canUseBulkActions}
          isAllSelected={isAllSelected}
          isPartiallySelected={isPartiallySelected}
          onToggleSelectAll={toggleSelectAll}
        />
        <TableBody>
          {isLoading ? (
            <LoadingState />
          ) : (
            <EmptyStateMessage 
              viewMode={viewMode}
              hasNotifications={notifications.length > 0}
              hasFilteredResults={displayedNotifications.length > 0}
            />
          )}
          {displayedNotifications.map((notification) => (
            <NotificationListItem
              key={notification.id}
              notification={notification}
              formatDate={formatDate}
              onNotificationClick={handleNotificationClick}
              onArchive={handleArchive}
              onRestore={handleRestore}
              onDelete={handleDelete}
              onSendWebhook={handleSendWebhook}
              isArchived={viewMode === "archived"}
              showBulkSelect={canUseBulkActions}
              isSelected={isSelected(notification.id)}
              onToggleSelect={toggleSelectItem}
              unreadChatCount={getUnreadChatCount?.(notification.id) ?? 0}
            />
          ))}
        </TableBody>
      </Table>
      {/* Fixed scrollbar */}
      {showFixedScrollbar && (
        <div
          ref={stickyScrollRef}
          onScroll={handleStickyScroll}
          className="sticky-scrollbar"
          style={{ left: containerRect.left, width: containerRect.width }}
        >
          <div style={{ width: scrollWidth, height: 1 }} />
        </div>
      )}
    </div>
  );
};

export default NotificationTable;